<template>
  <div class="text-base relative z-10" :style="theme ? { color: theme.base.text.base } : {}" ref="$container">
    <div class="flex overflow-hidden" :class="[ isFull ? '': 'space-x-4' ]">
      <component
        :is="component(item)"
        class="font-semi text-center pb-2.5 border-b-2 transition duration-75 shrink-0 whitespace-nowrap cursor-pointer flex items-center space-x-2"
        v-for="item in items.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value))"
        draggable="false"
        :to="item.to"
        :class="[
          itemsOverflowing.find(i => i.value === item.value) ? 'opacity-0 pointer-events-none' : '',
          isFull ? 'flex-1 text-center justify-center' : '',
          item.to?.name === (route?.name ?? '') || item.value === modelValue ? getStyle(styles, 'active') : getStyle(styles, 'inactive')
        ]"
        :style="theme ? (modelValue === item.value ? { borderColor: theme.base.text.base } : { borderColor: theme.base.background.border }) : {}"
        @click="() => modelValue = item.value"
        :data-id="item.value"
        ref="$items"
      >
        <ImageEmoji
          :name="item.image"
          class="size-5 transition -mr-1"
          size="sm"
          :class="[
            item.to?.name === (route?.name ?? '') || item.value === modelValue ? 'saturate-100' : 'saturate-[0.5] opacity-60'
          ]"
          v-if="item.image"
        />
        <IconBase
          :name="item.icon + (item.to?.name === (route?.name ?? '') || item.value === modelValue ? '-fill' : '')"
          :width="18"
          :class="[
            item.to?.name === (route?.name ?? '') || item.value === modelValue ? '' : 'opacity-60'
          ]"
          v-else-if="item.icon"
        />

        <p class="leading-tight">
          {{ item.label }}
        </p>

        <ChipRound size="xs" :style="theme ? { backgroundColor: theme.base.text.base, color: theme.base.text.light } : {}" v-if="item.count !== undefined">
          {{ item.count }}
        </ChipRound>
      </component>
      <div
        class="font-semi text-center space-y-1 pb-2.5 border-b-2 flex items-center transition shrink-0 whitespace-nowrap absolute bottom-0 right-2 cursor-pointer"
        :class="[
          itemsOverflowing.find(i => i.value === modelValue) ? getStyle(styles, 'active') : getStyle(styles, 'inactive'),
          itemsOverflowing.length > 0 ? '' : 'opacity-0 pointer-events-none'
        ]"
        ref="$more"
        @click="() => isMenuActive = true"
      >
        <p class="leading-tight">{{ $t('actions.more') }}</p>

        <IconBase name="cute/angle-right-fill" class="rotate-90 ml-1 -translate-y-[1px] leading-none" :width="16" />
      </div>
    </div>

    <div class="fixed top-0 left-0 w-full h-full z-[200] flex items-center justify-center px-8" v-if="isMenuActive">
      <div class="absolute top-0 left-0 w-full h-full z-[199] bg-charcoal-950/50 dark:bg-dark-600/80" @click="() => isMenuActive = false" />

      <div class="bg-white dark:bg-dark-920 p-2.5 relative text-left text-md rounded-lg border border-urchin-950/10 right-2 shadow-xl space-y-0.5 max-h-[50vh] max-w-xs w-full mx-auto z-[201]">
        <component
          :is="component(item)"
          class="font-medium space-y-1.5 px-4 py-3.5 text-urchin-950 dark:text-urchin-50 bg-urchin-100 dark:bg-dark-800 rounded-md block transition duration-75 shrink-0 whitespace-nowrap cursor-pointer"
          v-for="item in itemsOverflowing"
          :to="item.to"
          @click="() => { modelValue = item.value; isMenuActive = false }"
        >
          {{ item.label }}
        </component>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { defineStyles, getStyle } from '@/utils/useStyles'
import { Theme } from '@/classes'

type NavItem = {
  label: string
  value: string
  image?: string
  icon?: string
  count?: number 
  isHidden?: boolean
  to?: RouteLocationRaw
}

const props = withDefaults(defineProps<{
  color?: 'base' | 'flamingo' | 'urchin' | 'inherit' | 'white'
  order?: string[]
  isFull?: boolean
  queryPrefix?: string
  items?: NavItem[]
  theme?: Theme | null
}>(), {
  color: 'inherit',
  isFull: false,
  queryPrefix: '',
  theme: null,
  order: () => [],
  items: () => []
})

const { order } = toRefs(props)

const route = useRoute()
const router = useRouter()
const modelValue = defineModel('modelValue', { default: '' })

const { isComponentActive } = useKeepAlive({
  onStart: () => {
    if (route.query[props.queryPrefix]) modelValue.value = route.query[props.queryPrefix] as string
  }
})

const hasRoutes = computed(() => props.items.some(item => item.to))

watch(modelValue, () => {
  if (hasRoutes.value && isComponentActive.value && props.queryPrefix) {
    router.push({ query: { ...route.query, [props.queryPrefix]: modelValue.value } })
  }
}, { immediate: true })

watch(() => route?.query[props.queryPrefix], (value) => {
  if (hasRoutes.value && isComponentActive.value && value !== modelValue.value) {
    modelValue.value = value as string
  }
})

const isMenuActive = ref(false)

const styles = computed(() => defineStyles({
  active: {
    color: {
      flamingo: 'border-flamingo-950 text-flamingo-950',
      urchin: 'text-urchin-900 border-urchin-900 dark:text-urchin-100 dark:border-urchin-100',
      white: 'border-white text-white',
      inherit: '',
      base: 'border-light-950 dark:border-dark-50 text-light-950 dark:text-dark-50',
    }
  },
  inactive: {
    color: {
      flamingo: 'border-flamingo-950/0 text-flamingo-950/30',
      urchin: 'border-urchin-950/0 text-urchin-900/30 dark:text-urchin-100/30 dark:border-urchin-100/0',
      white: 'border-white/10 text-white/50',
      inherit: '',
      base: 'border-light-300/50 text-light-950/50 dark:text-dark-50/50 dark:border-dark-400/50',
    }
  }
}, props))

const component = (item: { to?: RouteLocationRaw }) => {

  return item.to ? resolveComponent('NuxtLink') : 'div'
}

const $container = ref()
const $items = ref()
const $more = ref()

const items = computed(() => props.items.filter(item => !item.isHidden).map(item => ({
  ...item,
  $el: ($items.value ?? []).find(($el: any) => {
    return ($el?.$el ?? $el)?.dataset?.id === item.value
  })
})))

const itemsProps = computed(() => props.items)

const { width } = useElementSize($container)
const { width: widthMore } = useElementSize($more)

const itemsOverflowing: Ref<NavItem[]> = ref([])

const onUpdate = () => {
  if (isComponentActive.value && !props.isFull) {
    itemsOverflowing.value = []
    let widthTotal = 0

    items.value.forEach(item => {
      if (item.$el) {
        widthTotal += (item.$el?.$el ?? item.$el).offsetWidth

        if (widthTotal > (width.value - (widthMore.value + 54))) {
          itemsOverflowing.value.push(item)
        }
      }
    })

  }
}

watchThrottled([ width, order, itemsProps ], () => {
  setTimeout(() => onUpdate(), 10)
}, { throttle: 200 })

onMounted(() => {
  setTimeout(() => onUpdate(), 10)
})

onBeforeUnmount(() => {
  if (props.queryPrefix) router.replace({ query: { ...route.query, [props.queryPrefix]: undefined } })
})

</script>